// Import React helpers
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Import portal helper
import { createPortal } from "react-dom";

// Import CSS
import "./ColourPicker.css";
import CreateButton from "./CreateButton";



// Convert a number into a safe 0 to 255 range
const ClampRgbValue = (value) => {

    // Return the clamped value
    return Math.min(255, Math.max(0, Math.round(value)));

};



// Convert HSV to RGB
const ConvertHsvToRgb = (hue, saturation, value) => {

    // Convert saturation to decimal
    const sat = saturation / 100;

    // Convert value to decimal
    const val = value / 100;

    // Calculate chroma
    const chroma = val * sat;

    // Calculate hue section
    const hueSection = hue / 60;

    // Calculate second largest component
    const secondComponent = chroma * (1 - Math.abs((hueSection % 2) - 1));

    // Calculate match value
    const match = val - chroma;

    // Create initial red
    let red = 0;

    // Create initial green
    let green = 0;

    // Create initial blue
    let blue = 0;

    // Handle first hue section
    if (hueSection >= 0 && hueSection < 1) {

        // Set red
        red = chroma;

        // Set green
        green = secondComponent;

    } else if (hueSection >= 1 && hueSection < 2) {

        // Set red
        red = secondComponent;

        // Set green
        green = chroma;

    } else if (hueSection >= 2 && hueSection < 3) {

        // Set green
        green = chroma;

        // Set blue
        blue = secondComponent;

    } else if (hueSection >= 3 && hueSection < 4) {

        // Set green
        green = secondComponent;

        // Set blue
        blue = chroma;

    } else if (hueSection >= 4 && hueSection < 5) {

        // Set red
        red = secondComponent;

        // Set blue
        blue = chroma;

    } else {

        // Set red
        red = chroma;

        // Set blue
        blue = secondComponent;

    }

    // Return rgb values
    return {
        red: ClampRgbValue((red + match) * 255),
        green: ClampRgbValue((green + match) * 255),
        blue: ClampRgbValue((blue + match) * 255),
    };

};



// Convert RGB to HEX
const ConvertRgbToHex = (red, green, blue, alpha = null) => {

    // Convert red to hex
    const redHex = ClampRgbValue(red).toString(16).padStart(2, "0");

    // Convert green to hex
    const greenHex = ClampRgbValue(green).toString(16).padStart(2, "0");

    // Convert blue to hex
    const blueHex = ClampRgbValue(blue).toString(16).padStart(2, "0");

    // Convert alpha percentage to hex when provided
    const alphaHex = alpha === null
        ? ""
        : ClampRgbValue((alpha / 100) * 255).toString(16).padStart(2, "0");

    // Return formatted HEX
    return `#${redHex}${greenHex}${blueHex}${alphaHex}`.toUpperCase();

};




// Convert HSV to HEX
const ConvertHsvToHex = (hue, saturation, value, alpha = null) => {

    // Convert HSV to RGB
    const rgb = ConvertHsvToRgb(hue, saturation, value);

    // Convert RGB to HEX including optional alpha
    return ConvertRgbToHex(rgb.red, rgb.green, rgb.blue, alpha);

};




// Convert HEX to RGB
const ConvertHexToRgb = (hex) => {

    // Remove hash
    const cleanHex = hex.replace("#", "");

    // Parse red
    const red = parseInt(cleanHex.substring(0, 2), 16);

    // Parse green
    const green = parseInt(cleanHex.substring(2, 4), 16);

    // Parse blue
    const blue = parseInt(cleanHex.substring(4, 6), 16);

    // Return rgb values
    return { red, green, blue };

};



// Convert RGB to HSV
const ConvertRgbToHsv = (red, green, blue) => {

    // Convert red to decimal
    const redDecimal = red / 255;

    // Convert green to decimal
    const greenDecimal = green / 255;

    // Convert blue to decimal
    const blueDecimal = blue / 255;

    // Get max channel
    const max = Math.max(redDecimal, greenDecimal, blueDecimal);

    // Get min channel
    const min = Math.min(redDecimal, greenDecimal, blueDecimal);

    // Calculate delta
    const delta = max - min;

    // Create initial hue
    let hue = 0;

    // Calculate hue when there is colour difference
    if (delta !== 0) {

        // Handle red max
        if (max === redDecimal) {

            // Calculate red hue
            hue = 60 * (((greenDecimal - blueDecimal) / delta) % 6);

        } else if (max === greenDecimal) {

            // Calculate green hue
            hue = 60 * ((blueDecimal - redDecimal) / delta + 2);

        } else {

            // Calculate blue hue
            hue = 60 * ((redDecimal - greenDecimal) / delta + 4);

        }

    }

    // Keep hue positive
    if (hue < 0) {

        // Add full circle
        hue += 360;

    }

    // Calculate saturation
    const saturation = max === 0 ? 0 : (delta / max) * 100;

    // Calculate value
    const value = max * 100;

    // Return hsv values
    return {
        hue: Math.round(hue),
        saturation: Math.round(saturation),
        value: Math.round(value),
    };

};



// Normalise HEX input
const NormalizeHexValue = (rawValue) => {

    // Return null for empty values
    if (!rawValue) {
        return null;
    }

    // Trim whitespace and uppercase
    const trimmedValue = rawValue.trim().toUpperCase();

    // Add hash if missing
    const valueWithHash = trimmedValue.startsWith("#") ? trimmedValue : `#${trimmedValue}`;

    // Match 3, 4, 6 or 8 character HEX values
    const shortHexRegex = /^#([0-9A-F]{3}|[0-9A-F]{4})$/;

    // Match full HEX values with optional alpha
    const fullHexRegex = /^#([0-9A-F]{6}|[0-9A-F]{8})$/;

    // Expand short HEX values
    if (shortHexRegex.test(valueWithHash)) {

        // Get characters without hash
        const hexCharacters = valueWithHash.slice(1).split("");

        // Double each character
        return `#${hexCharacters.map((character) => character + character).join("")}`;

    }

    // Return valid full HEX values
    if (fullHexRegex.test(valueWithHash)) {
        return valueWithHash;
    }

    // Return invalid marker
    return "INVALID";

};




// Get a portal root safely
const GetFloatingUiRoot = () => {

    // Try to find the preferred portal root
    const floatingRoot = document.getElementById("floating-ui-root");

    // Return preferred root if it exists
    if (floatingRoot) {

        // Return floating root
        return floatingRoot;

    }

    // Return body as a fallback
    return document.body;

};



// Exported component in PascalCase
export const ColorPickerSmall = ({
    value,
    onChange,
    onOpen = null,
    onClose = null,
    label = "Colour",
    disabled = false,
    title = "Colour Picker",
    size = "m",
    setClearColourAsTransparent = false,
    displayPickerLocationTopBottom = "top",
    displayPickerLocationLeftRight = "left",
    allowNullOrTransparent = false,
    forceOpen = false,
    renderInline = false,
    allowAlpha = false,
    
}) => {

    // Ref for the whole trigger wrapper
    const wrapperRef = useRef(null);

    // Ref for the trigger button
    const triggerRef = useRef(null);

    // Ref for the popover
    const popoverRef = useRef(null);

    // Ref for the colour field
    const colourFieldRef = useRef(null);

    // Ref for the hue slider
    const hueSliderRef = useRef(null);

    // Store whether the saturation and brightness field is currently being dragged
    const isColourFieldDraggingRef = useRef(false);

    // Store the current hue independently from React render timing
    const hueRef = useRef(0);

    // Store whether the popover is open
    const [isOpen, setIsOpen] = useState(false);

    // Store the hex input value
    const [hexInput, setHexInput] = useState(value || "");

    // Store input error text
    const [inputError, setInputError] = useState("");

    // Store hue
    const [hue, setHue] = useState(0);

    // Store saturation
    const [saturation, setSaturation] = useState(100);

    // Store value / brightness
    const [brightness, setBrightness] = useState(100);

    // Store alpha as a percentage
    const [alpha, setAlpha] = useState(100);

    // Store portal popover position
    const [popoverPosition, setPopoverPosition] = useState({
        top: 0,
        left: 0,
        visibility: "hidden",
    });



    // Keep the current hue available to pointer event handlers
    useEffect(() => {

        // Store the latest hue
        hueRef.current = hue;

    }, [hue]);


    // Work out whether the picker should be open
    const pickerIsOpen = forceOpen || isOpen;

    // Work out if the current value is a real colour
    const hasColour = Boolean(value && value !== "#FFFFFF00");


    // Work out the displayed colour
    const displayedColour = hasColour ? value : "#FFFFFF00";

    // Get the current hue base colour
    const hueBaseColour = useMemo(() => {

        // Return the full saturation hue colour
        return ConvertHsvToHex(hue, 100, 100);

    }, [hue]);



    // Close the popover
    const closePopover = useCallback(() => {

        // Close popover
        setIsOpen(false);

        // Clear error
        setInputError("");

        // Reset input to current value
        setHexInput(value || "");

        // If there is an onClose funtion execute it
        if (typeof onClose === "function") {

            onClose();

        }

    }, [onClose, value]);



    // Commit value to parent
    const commitColour = (nextValue) => {

        // Clear error
        setInputError("");

        // Set input value
        setHexInput(nextValue || "");

        // Call parent change handler if available
        if (typeof onChange === "function") {

            // Send new value to parent
            onChange(nextValue || null);

        }

    };



    // Commit HSV as HEX with optional alpha
    const commitHsvColour = (
        nextHue,
        nextSaturation,
        nextBrightness,
        nextAlpha = alpha
    ) => {

        // Convert selected colour to HEX
        const nextHex = ConvertHsvToHex(
            nextHue,
            nextSaturation,
            nextBrightness,
            allowAlpha ? nextAlpha : null
        );

        // Commit colour
        commitColour(nextHex);

    };





    // Sync picker from parent value
    useEffect(() => {

        // Set input from parent value
        setHexInput(value || "");

        // Clear errors
        setInputError("");

        // Do not sync HSV while the user is dragging the colour field
        if (isColourFieldDraggingRef.current) {

            // Early return
            return;

        }

        // Do nothing for empty values
        if (!value || value === "transparent") {

            // Early return
            return;

        }

        // Normalise parent value
        const normalisedValue = NormalizeHexValue(value);

        // Do nothing if invalid
        if (!normalisedValue || normalisedValue === "INVALID") {

            // Early return
            return;

        }

        // Convert hex to rgb
        const rgb = ConvertHexToRgb(normalisedValue);


        // Extract alpha characters when present
        const alphaHex = normalisedValue.length === 9
            ? normalisedValue.slice(7, 9)
            : "FF";

        // Convert alpha into a percentage
        const nextAlpha = Math.round((parseInt(alphaHex, 16) / 255) * 100);

        // Store alpha
        setAlpha(nextAlpha);


        // Convert rgb to hsv
        const hsv = ConvertRgbToHsv(
            rgb.red,
            rgb.green,
            rgb.blue
        );

        // Check whether the colour contains meaningful hue information
        const hasMeaningfulHue =
            hsv.saturation > 0 &&
            hsv.value > 0;

        // Only update hue for colours with meaningful hue information
        if (hasMeaningfulHue) {

            // Store hue in the ref immediately
            hueRef.current = hsv.hue;

            // Store hue in state
            setHue(hsv.hue);

        }

        // Store saturation
        setSaturation(hsv.saturation);

        // Store brightness
        setBrightness(hsv.value);

    }, [value]);



    // Close when clicking outside trigger and popover
    useEffect(() => {

        // Do nothing for inline pickers or when closed
        if (renderInline || !pickerIsOpen) {

            // Early return
            return;

        }

        // Handle mouse down
        const handleDocumentMouseDown = (event) => {

            // Check if click is inside trigger wrapper
            const clickedInsideWrapper = wrapperRef.current?.contains(event.target);

            // Check if click is inside popover
            const clickedInsidePopover = popoverRef.current?.contains(event.target);

            // Do nothing if inside either area
            if (clickedInsideWrapper || clickedInsidePopover) {

                // Early return
                return;

            }

            // Close popover
            closePopover();

        };

        // Add event listener
        document.addEventListener("mousedown", handleDocumentMouseDown);

        // Cleanup listener
        return () => {

            // Remove event listener
            document.removeEventListener("mousedown", handleDocumentMouseDown);

        };

    }, [pickerIsOpen, closePopover, renderInline]);



    // Close on Escape
    useEffect(() => {

        // Do nothing if closed
        if (!pickerIsOpen) {

            // Early return
            return;

        }

        // Handle key down
        const handleKeyDown = (event) => {

            // Check for Escape
            if (event.key === "Escape") {

                // Close popover
                closePopover();

            }

        };

        // Add event listener
        document.addEventListener("keydown", handleKeyDown);

        // Cleanup listener
        return () => {

            // Remove event listener
            document.removeEventListener("keydown", handleKeyDown);

        };

    }, [pickerIsOpen, closePopover]);



    // Position the popover
    useEffect(() => {

        // Do nothing when rendered inline or closed
        if (renderInline || !pickerIsOpen) {

            // Early return
            return;

        }

        // Update popover position
        const updatePopoverPosition = () => {

            // Do nothing without trigger
            if (!triggerRef.current) {

                // Early return
                return;

            }

            // Get trigger rect
            const triggerRect = triggerRef.current.getBoundingClientRect();

            // Get popover rect
            const popoverRect = popoverRef.current?.getBoundingClientRect();

            // Set gap
            const gap = 8;

            // Set viewport padding
            const viewportPadding = 8;

            // Get popover width
            const popoverWidth = popoverRect?.width || 320;

            // Get popover height
            const popoverHeight = popoverRect?.height || 420;

            // Calculate left position
            let nextLeft = displayPickerLocationLeftRight === "right" ? triggerRect.right - popoverWidth : triggerRect.left;

            // Calculate top position
            let nextTop = displayPickerLocationTopBottom === "bottom" ? triggerRect.top - popoverHeight - gap : triggerRect.bottom + gap;

            // Prevent right overflow
            if (nextLeft + popoverWidth > window.innerWidth - viewportPadding) {

                // Shift left
                nextLeft = window.innerWidth - popoverWidth - viewportPadding;

            }

            // Prevent left overflow
            if (nextLeft < viewportPadding) {

                // Shift right
                nextLeft = viewportPadding;

            }

            // Prevent bottom overflow
            if (nextTop + popoverHeight > window.innerHeight - viewportPadding) {

                // Place above trigger
                nextTop = triggerRect.top - popoverHeight - gap;

            }

            // Prevent top overflow
            if (nextTop < viewportPadding) {

                // Shift down
                nextTop = viewportPadding;

            }

            // Store calculated position
            setPopoverPosition({
                top: nextTop,
                left: nextLeft,
                visibility: "visible",
            });

        };

        // Update immediately
        updatePopoverPosition();

        // Update after paint
        requestAnimationFrame(updatePopoverPosition);

        // Listen to resize
        window.addEventListener("resize", updatePopoverPosition);

        // Listen to scroll
        window.addEventListener("scroll", updatePopoverPosition, true);

        // Cleanup listeners
        return () => {

            // Remove resize listener
            window.removeEventListener("resize", updatePopoverPosition);

            // Remove scroll listener
            window.removeEventListener("scroll", updatePopoverPosition, true);

        };

    }, [pickerIsOpen, renderInline, displayPickerLocationTopBottom, displayPickerLocationLeftRight]);



    // Toggle open state
    const handleToggleOpen = () => {

        // Do nothing if disabled or forced open
        if (disabled || forceOpen) {

            // Early return
            return;

        }

        // Work out if opening
        const willOpen = !isOpen;

        // Call open callback
        if (willOpen && typeof onOpen === "function") {

            // Call parent
            onOpen();

        }

        // Toggle open
        setIsOpen((previousValue) => !previousValue);

        // Clear error
        setInputError("");

        // Reset input
        setHexInput(value || "");

    };



    // Handle colour field position
    const updateColourFromFieldPointer = (clientX, clientY) => {

        // Do nothing without field
        if (!colourFieldRef.current) {

            // Early return
            return;

        }

        // Get field bounds
        const fieldRect = colourFieldRef.current.getBoundingClientRect();

        // Calculate x position
        const xPosition = Math.min(Math.max(clientX - fieldRect.left, 0), fieldRect.width);

        // Calculate y position
        const yPosition = Math.min(Math.max(clientY - fieldRect.top, 0), fieldRect.height);

        // Convert x to saturation
        const nextSaturation = Math.round((xPosition / fieldRect.width) * 100);

        // Convert y to brightness
        const nextBrightness = Math.round(100 - (yPosition / fieldRect.height) * 100);

        // Store saturation
        setSaturation(nextSaturation);

        // Store brightness
        setBrightness(nextBrightness);

        // Commit selected colour using the preserved hue
        commitHsvColour(
            hueRef.current,
            nextSaturation,
            nextBrightness
        );

    };



    // Start field dragging
    const handleColourFieldMouseDown = (event) => {

        // Prevent text selection
        event.preventDefault();

        // Mark the colour field as being dragged
        isColourFieldDraggingRef.current = true;

        // Update from first click
        updateColourFromFieldPointer(
            event.clientX,
            event.clientY
        );

        // Handle mouse move
        const handleMouseMove = (moveEvent) => {

            // Update while dragging
            updateColourFromFieldPointer(
                moveEvent.clientX,
                moveEvent.clientY
            );

        };

        // Handle mouse up
        const handleMouseUp = () => {

            // Stop treating parent updates as drag-generated updates
            isColourFieldDraggingRef.current = false;

            // Remove move listener
            document.removeEventListener(
                "mousemove",
                handleMouseMove
            );

            // Remove up listener
            document.removeEventListener(
                "mouseup",
                handleMouseUp
            );

        };

        // Add move listener
        document.addEventListener(
            "mousemove",
            handleMouseMove
        );

        // Add up listener
        document.addEventListener(
            "mouseup",
            handleMouseUp
        );

    };



    // Handle hue slider change
    const handleHueChange = (event) => {

        // Get hue as number
        const nextHue = Number(event.target.value);

        // Store hue immediately for pointer handlers
        hueRef.current = nextHue;

        // Store hue
        setHue(nextHue);

        // Commit selected colour
        commitHsvColour(
            nextHue,
            saturation,
            brightness
        );

    };




    // Handle alpha slider change
    const handleAlphaChange = (event) => {

        // Get alpha percentage
        const nextAlpha = Number(event.target.value);

        // Store alpha
        setAlpha(nextAlpha);

        // Commit colour with updated alpha
        commitHsvColour(
            hue,
            saturation,
            brightness,
            nextAlpha
        );

    };




    // Handle hex input change
    const handleHexInputChange = (event) => {

        // Store input
        setHexInput(event.target.value);

        // Clear error
        setInputError("");

    };



    // Handle hex input blur
    const handleHexInputBlur = () => {

        // Normalise input
        const normalisedValue = NormalizeHexValue(hexInput);


        // Check whether alpha is allowed
        if (
            !allowAlpha &&
            normalisedValue &&
            normalisedValue !== "INVALID" &&
            normalisedValue.length === 9
        ) {

            // Show validation error
            setInputError("Transparency is not allowed for this colour");

            // Reset input to current value
            setHexInput(value || "");

            // Stop processing
            return;

        }


        // Handle empty input
        if (normalisedValue === null) {

            // Commit null
            commitColour(null);

            // Done
            return;

        }

        // Handle invalid input
        if (normalisedValue === "INVALID") {

            // Show the appropriate validation message
            setInputError(
                allowAlpha
                    ? "Use #RGB, #RGBA, #RRGGBB or #RRGGBBAA"
                    : "Use #RGB or #RRGGBB"
            );

            // Reset input
            setHexInput(value || "");

            // Done
            return;

        }

        // Convert hex to rgb
        const rgb = ConvertHexToRgb(normalisedValue);

        // Convert rgb to hsv
        const hsv = ConvertRgbToHsv(rgb.red, rgb.green, rgb.blue);

        // Check whether the colour contains meaningful hue information
        const hasMeaningfulHue = hsv.saturation > 0 && hsv.value > 0;

        // Only replace the hue when the colour has meaningful hue information
        if (hasMeaningfulHue) {

            // Store hue
            setHue(hsv.hue);

        }

        // Store saturation
        setSaturation(hsv.saturation);

        // Store brightness
        setBrightness(hsv.value);

        // Commit value
        commitColour(normalisedValue);

    };



    // Handle hex input key down
    const handleHexInputKeyDown = (event) => {

        // Handle enter
        if (event.key === "Enter") {

            // Prevent form submit
            event.preventDefault();

            // Blur input
            event.currentTarget.blur();

        }

    };



    // Handle clear/null click
    const handleClearClick = () => {

        // Check if clear should become transparent
        if (setClearColourAsTransparent) {

            // Commit transparent
            commitColour("#FFFFFF00");

        } else {

            // Commit null
            commitColour(null);

        }

    };



    // Build popover element
    const popoverElement = pickerIsOpen ? (

        <div
            ref={popoverRef}
            className={`color-picker-small__popover ${renderInline ? "color-picker-small__popover--inline" : "color-picker-small__popover--portal"}`}
            role="dialog"
            aria-label="Colour picker"
            style={renderInline ? undefined : {
                position: "fixed",
                top: `${popoverPosition.top}px`,
                left: `${popoverPosition.left}px`,
                visibility: popoverPosition.visibility,
                zIndex: 1002,
            }}
        >

            <div className="color-picker-small__preview-row">

                <div className={'color-picker-small__preview-checkered-background'}></div>

                <div
                    className={`color-picker-small__preview ${hasColour ? "color-picker-small__preview--has-colour" : "color-picker-small__preview--empty"}`}
                    style={hasColour ? { backgroundColor: displayedColour } : undefined}
                />

                <div className="color-picker-small__preview-text">

                    <strong className="color-picker-small__preview-title">

                        {hasColour ? value : "No colour selected"}

                    </strong>

                    <span className="color-picker-small__preview-subtitle">

                        Drag, slide, type HEX, or clear.

                    </span>

                </div>

            </div>

            <div
                ref={colourFieldRef}
                className="color-picker-small__colour-field"
                style={{ backgroundColor: hueBaseColour }}
                onMouseDown={handleColourFieldMouseDown}
                role="slider"
                aria-label="Choose saturation and brightness"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={saturation}
                aria-valuetext={`Saturation ${saturation}%, brightness ${brightness}%`}
                tabIndex={0}
            >

                <span className="color-picker-small__colour-field-white" />

                <span className="color-picker-small__colour-field-black" />

                <span
                    className="color-picker-small__colour-field-handle"
                    style={{
                        left: `${saturation}%`,
                        top: `${100 - brightness}%`,
                    }}
                />

            </div>

            <label className="color-picker-small__slider-label">

                <span className="color-picker-small__slider-title">

                    Hue

                </span>

                <input
                    ref={hueSliderRef}
                    className="color-picker-small__hue-slider"
                    type="range"
                    min="0"
                    max="359"
                    value={hue}
                    onChange={handleHueChange}
                    aria-label="Hue"
                />

            </label>

            {allowAlpha && (

                <label className="color-picker-small__slider-label">

                    {/* Display alpha slider title */}
                    <span className="color-picker-small__slider-title">
                        Opacity: {alpha}%
                    </span>

                    {/* Allow opacity adjustment */}
                    <input
                        className="color-picker-small__alpha-slider"
                        type="range"
                        min="0"
                        max="100"
                        value={alpha}
                        onChange={handleAlphaChange}
                        aria-label="Opacity"
                        style={{
                           
                            // Overlay the opacity gradient on top of the checkerboard
                            backgroundImage: `
                                linear-gradient(to right, transparent, ${ConvertHsvToHex(hue, saturation, brightness)}),
                                linear-gradient(45deg, #ddd 25%, transparent 25%),
                                linear-gradient(-45deg, #ddd 25%, transparent 25%),
                                linear-gradient(45deg, transparent 75%, #ddd 75%),
                                linear-gradient(-45deg, transparent 75%, #ddd 75%)
                            `,

                            // Set the size of each background layer
                            backgroundSize: "100% 100%, 12px 12px, 12px 12px, 12px 12px, 12px 12px",

                            // Position the checkerboard layers
                            backgroundPosition: "0 0, 0 0, 0 6px, 6px -6px, -6px 0",

                            // Provide the white squares behind the grey squares
                            backgroundColor: "#fff"
                            
                        }}
                    />

                </label>

            )}

            <div className="color-picker-small__input-row">

                <label className="color-picker-small__input-label">

                    <input
                        type="text"
                        className={`color-picker-small__input ${inputError ? "color-picker-small__input--error" : ""}`}
                        placeholder={allowAlpha ? "#RRGGBBAA" : "#RRGGBB"}
                        value={hexInput}
                        onChange={handleHexInputChange}
                        onBlur={handleHexInputBlur}
                        onKeyDown={handleHexInputKeyDown}
                        spellCheck={false}
                        autoComplete="off"
                    />

                </label>

                {allowNullOrTransparent &&    

                    <CreateButton
                        type="button"
                        title="Clear"
                        size="s"
                        plain={true}
                        className="color-picker-small__clear-button"
                        onClick={handleClearClick}
                    />

                }

                {/* Give normal popover pickers an explicit way to finish and close */}
                {!renderInline && !forceOpen &&

                    <CreateButton
                        type="button"
                        title="OK"
                        size="s"
                        onClick={closePopover}
                    />

                }

            </div>

            {inputError && (

                <div className="color-picker-small__error warning-message">

                    {inputError}

                </div>

            )}

        </div>

    ) : null;



    // Render component
    return (

        <>

            <div className={`color-picker-small ${disabled ? "color-picker-small--disabled" : ""}`} ref={wrapperRef} title={title}>

                {label && (

                    <div className="color-picker-small__label">

                        {label}

                    </div>

                )}

                <button
                    ref={triggerRef}
                    type="button"
                    className="color-picker-small__trigger"
                    onClick={handleToggleOpen}
                    aria-haspopup="dialog"
                    aria-expanded={isOpen}
                    disabled={disabled}
                >

                    <span
                        className={`color-picker-small__dot dot-size__${size} ${hasColour ? "color-picker-small__dot--has-colour" : "color-picker-small__dot--no-colour"}`}
                        style={hasColour ? { backgroundColor: displayedColour } : undefined}
                        aria-label={hasColour ? `Selected colour ${value}` : "No colour selected"}
                    />

                </button>

            </div>

            {popoverElement && (renderInline ? popoverElement : createPortal(popoverElement, GetFloatingUiRoot()))}

        </>

    );

};